import React, { useEffect, useState, useCallback, useRef } from 'react';

import {
  Box, IconButton, Dialog, DialogActions, DialogContent,
  DialogTitle, Button, TablePagination, CircularProgress,
  Typography, Grid, Card, CardMedia, CardContent, TextField, Divider, Chip, Select, MenuItem, Tabs, Tab
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';
import PageHeader1 from '../../components/common/PageHeader1';
import Autocomplete from '@mui/material/Autocomplete';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { toast } from 'react-toastify';
import { BsEye, BsDownload, BsPencil } from 'react-icons/bs';
import * as XLSX from 'xlsx';

const SEARCH_BY_OPTIONS = [
  { value: "orderId", label: "Order ID" },
  { value: "poojaName", label: "Pooja Name" },
  { value: "userName", label: "User Name" },
  { value: "astrologerName", label: "Astrologer Name" }
];

function PoojaBookings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialPageSize = parseInt(searchParams.get('limit') || '10', 10);

  const [paginationModel, setPaginationModel] = useState({
    page: Number.isFinite(initialPage) && initialPage >= 0 ? initialPage : 0,
    pageSize: Number.isFinite(initialPageSize) && initialPageSize > 0 ? initialPageSize : 10,
  });

  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState(searchParams.get("search") || "");
  const [searchBy, setSearchBy] = useState(searchParams.get("searchBy") || SEARCH_BY_OPTIONS[0].value);
  const [appliedSearch, setAppliedSearch] = useState(searchParams.get("search") || "");
  const [appliedSearchBy, setAppliedSearchBy] = useState(searchParams.get("searchBy") || SEARCH_BY_OPTIONS[0].value);
  const [searchStatus, setSearchStatus] = useState(searchParams.get("status") || "");
  const [appliedStatus, setAppliedStatus] = useState(searchParams.get("status") || "");
  const todayStr = new Date().toISOString().split("T")[0];
  const urlStartDate = searchParams.get("startDate") || "";
  const urlEndDate = searchParams.get("endDate") || "";
  const urlSearch = searchParams.get("search") || "";
  const [isDefaultToday, setIsDefaultToday] = useState(!urlStartDate && !urlEndDate && !urlSearch);
  const [startDate, setStartDate] = useState(urlStartDate);
  const [endDate, setEndDate] = useState(urlEndDate);
  const [appliedStartDate, setAppliedStartDate] = useState(urlStartDate);
  const [appliedEndDate, setAppliedEndDate] = useState(urlEndDate);
  const [exportLoading, setExportLoading] = useState(false);

  // View Dialog State
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [selectedPooja, setSelectedPooja] = useState(null);
  const [proofsState, setProofsState] = useState([]);

  // Reset Video Dialog State
  const [resetVideoDialogOpen, setResetVideoDialogOpen] = useState(false);
  const [resetBookingId, setResetBookingId] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  // Edit Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: "",
    fullName: "",
    gotra: "",
    dateOfBirth: "",
    nakshatra: "",
    sankalp: "",
    purposeOfPooja: "",
    familyMemberNames: "",
    additionalNotes: ""
  });

  // ── Astrologer Search State ──
  const [astrologers, setAstrologers] = useState([]);
  const [astroLoading, setAstroLoading] = useState(false);
  const [astroSearch, setAstroSearch] = useState("");
  const [astroPage, setAstroPage] = useState(1);
  const [astroPagination, setAstroPagination] = useState({ totalDocs: 0, totalPages: 1 });

  // ── Reassign Dialog State ──
  const [mapOpen, setMapOpen] = useState(false);
  const [mapRowData, setMapRowData] = useState(null);
  const mapRowDataRef = useRef(null);
  useEffect(() => {
    mapRowDataRef.current = mapRowData;
  }, [mapRowData]);
  const [mapLoading, setMapLoading] = useState(false);
  const [selectedAstrologerId, setSelectedAstrologerId] = useState("");

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const pageParam = paginationModel.page + 1;
      const limitParam = paginationModel.pageSize;

      const searchParamStr = appliedSearch ? `&${appliedSearchBy}=${encodeURIComponent(appliedSearch)}` : "";
      const statusParamStr = appliedStatus ? `&status=${encodeURIComponent(appliedStatus)}` : "";

      const effectiveStartDate = appliedStartDate ? appliedStartDate : (isDefaultToday ? todayStr : "");
      const effectiveEndDate = appliedEndDate ? appliedEndDate : (isDefaultToday ? todayStr : "");

      const startDateStr = effectiveStartDate ? `&startDate=${encodeURIComponent(effectiveStartDate)}` : "";
      const endDateStr = effectiveEndDate ? `&endDate=${encodeURIComponent(effectiveEndDate)}` : "";

      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja/bookings?page=${pageParam}&limit=${limitParam}${searchParamStr}${statusParamStr}${startDateStr}${endDateStr}`, {
        headers: { Authorization: token }
      });
      const result = await response.json();

      if (response.ok) {
        const items = result?.data || [];
        const total = result?.paginationDetail?.totalDocs || items.length;
        setData(items);
        setTotalCount(total);
      } else {
        toast.error(result?.message || 'Failed to fetch pooja bookings');
      }
    } catch (error) {
      console.error("Error fetching pooja bookings:", error);
      toast.error('An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, appliedSearch, appliedSearchBy, appliedStatus, appliedStartDate, appliedEndDate, isDefaultToday, todayStr]);

  useEffect(() => {
    const params = {
      page: paginationModel.page,
      limit: paginationModel.pageSize,
    };
    if (appliedSearch) {
      params.search = appliedSearch;
      params.searchBy = appliedSearchBy;
    }
    if (appliedStatus) {
      params.status = appliedStatus;
    }
    if (appliedStartDate) {
      params.startDate = appliedStartDate;
    }
    if (appliedEndDate) {
      params.endDate = appliedEndDate;
    }
    setSearchParams(params, { replace: true });
    fetchBookings();
  }, [paginationModel, appliedSearch, appliedSearchBy, appliedStatus, appliedStartDate, appliedEndDate, isDefaultToday, fetchBookings, setSearchParams]);

  const handleApplySearch = () => {
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      toast.error("End Date must be greater than or equal to Start Date");
      return;
    }
    const hasSearch = Boolean(searchText.trim());
    const hasDates = Boolean(startDate || endDate);

    if (hasSearch || hasDates) {
      setIsDefaultToday(false);
    } else {
      setIsDefaultToday(true);
    }

    setAppliedSearch(searchText.trim());
    setAppliedSearchBy(searchBy);
    setAppliedStatus(searchStatus);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleResetSearch = () => {
    setSearchText("");
    setAppliedSearch("");
    setSearchBy(SEARCH_BY_OPTIONS[0].value);
    setAppliedSearchBy(SEARCH_BY_OPTIONS[0].value);
    setSearchStatus("");
    setAppliedStatus("");
    setStartDate("");
    setEndDate("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setIsDefaultToday(true);
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem("astroVani-admin-token");
      const searchParamStr = appliedSearch ? `&${appliedSearchBy}=${encodeURIComponent(appliedSearch)}` : "";
      const statusParamStr = appliedStatus ? `&status=${encodeURIComponent(appliedStatus)}` : "";

      const effectiveStartDate = appliedStartDate ? appliedStartDate : (isDefaultToday ? todayStr : "");
      const effectiveEndDate = appliedEndDate ? appliedEndDate : (isDefaultToday ? todayStr : "");

      const startDateStr = effectiveStartDate ? `&startDate=${encodeURIComponent(effectiveStartDate)}` : "";
      const endDateStr = effectiveEndDate ? `&endDate=${encodeURIComponent(effectiveEndDate)}` : "";
      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja/bookings?page=1&limit=10000${searchParamStr}${statusParamStr}${startDateStr}${endDateStr}`, {
        headers: { Authorization: token }
      });
      const result = await response.json();
      const exportData = result?.data || [];

      if (!exportData.length) {
        toast.error("No data found");
        return;
      }

      const rows = exportData.map((item) => ({
        "Order ID": item?.orderId || "-",
        "Pooja Name": item?.poojaId?.name || item?.poojaName || "-",
        "User Name (Account)": item?.userId?.fullName || "-",
        "Booking Name": item?.fullName || "-",
        "Gotra": item?.gotra || "-",
        "Nakshatra": item?.nakshatra || "-",
        "Family Members": item?.familyMemberNames || "-",
        "Purpose": item?.purposeOfPooja || "-",
        "Sankalp": item?.sankalp || "-",
        "Astrologer Name": item?.astrologerId?.fullName || item?.astrologerName || "-",
        "Status": item?.status || "-",
        "Payment Status": item?.paymentStatus || "-",
        "Verified By Admin": item?.verifiedByAdmin ? "Yes" : "No",
        "Completed By Astro": item?.isCompletedByAstro ? "Yes" : "No",
        "Media Uploaded": item?.isMediaUploaded ? "Yes" : "No",
        "Video Played": item?.isVideoPlayed ? "Yes" : "No",
        "Date": item?.createdAt ? new Date(item.createdAt).toLocaleString() : "-",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Pooja Bookings");
      XLSX.writeFile(workbook, "Pooja_Bookings.xlsx");
      toast.success("Export completed successfully");
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenVerifyDialog = (pooja) => {
    setSelectedPooja(pooja);
    const initialProofs = (pooja.proofUploads || pooja.proofs || []).map(p => ({
      url: p.url || p,
      adminApproved: p?.adminApproved !== undefined ? p?.adminApproved : false
    }));
    setProofsState(initialProofs);
    setVerifyDialogOpen(true);
  };

  const handleCloseVerifyDialog = () => {
    setVerifyDialogOpen(false);
    setSelectedPooja(null);
    setProofsState([]);
  };

  const fetchAstrologers = async (page = 1, search = "", currentPoojaId = null) => {
    try {
      setAstroLoading(true);
      const token = localStorage.getItem("astroVani-admin-token");
      const targetPoojaId = currentPoojaId || mapRowDataRef.current?.poojaId?._id || mapRowDataRef.current?.poojaId || mapRowData?.poojaId?._id || mapRowData?.poojaId;
      const finalPoojaId = typeof targetPoojaId === 'object' ? targetPoojaId?._id : targetPoojaId;
      if (!finalPoojaId) return;

      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/pooja/astrologers/${finalPoojaId}?page=${page}&limit=10${search ? `&search=${encodeURIComponent(search)}` : ""}`,
        { headers: { Authorization: token } }
      );
      const result = response.data;
      const rawList = result?.data || [];
      const mappedList = rawList.map((item) => {
        if (item.astrologerId && typeof item.astrologerId === 'object') {
          return {
            ...item.astrologerId,
            _id: item.astrologerId._id,
            mappingId: item._id,
            fullName: item.astrologerId.fullName,
            mobileNumber: item.astrologerId.mobileNumber,
            profileImg: item.astrologerId.profileImg,
            finalOfferUserPrice: item.finalOfferUserPrice,
            finalUserPrice: item.finalUserPrice,
            price: item.finalOfferUserPrice ?? item.finalUserPrice,
            poojaDescription: item.poojaDescription,
            companyShare: item.companyShare,
            astrologerShare: item.astrologerShare,
            rawMapping: item,
          };
        }
        return item;
      });
      setAstrologers(mappedList);
      setAstroPagination({
        totalDocs: result?.paginationDetail?.totalDocs || 0,
        totalPages: result?.paginationDetail?.totalPages || 1,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setAstroLoading(false);
    }
  };

  const debouncedAstroSearch = useCallback(
    debounce((value) => {
      setAstroPage(1);
      setAstroSearch(value);
      fetchAstrologers(1, value);
    }, 500),
    []
  );

  useEffect(() => {
    return () => debouncedAstroSearch.cancel();
  }, [debouncedAstroSearch]);

  const handleOpenMapping = (row) => {
    setMapRowData(row);
    mapRowDataRef.current = row;
    setSelectedAstrologerId("");
    setAstroPage(1);
    setAstroSearch("");
    const pId = row?.poojaId?._id || row?.poojaId;
    fetchAstrologers(1, "", pId);
    setMapOpen(true);
  };

  const handleMappingSubmit = async () => {
    if (!selectedAstrologerId) {
      toast.error("Please select an Astrologer");
      return;
    }
    setMapLoading(true);
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const payload = { newAstrologerId: selectedAstrologerId };
      await axios.patch(`${process.env.REACT_APP_BASEURL}/admin/pooja/reassign/${mapRowData._id || mapRowData.id}`, payload, {
        headers: { Authorization: token },
      });
      toast.success("Pooja reassigned successfully!");
      setMapOpen(false);
      fetchBookings();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to reassign pooja");
    } finally {
      setMapLoading(false);
    }
  };

  const handleResetVideoPlayed = (bookingId) => {
    setResetBookingId(bookingId);
    setResetVideoDialogOpen(true);
  };

  const confirmResetVideoPlayed = async () => {
    setResetLoading(true);
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      await axios.patch(`${process.env.REACT_APP_BASEURL}/admin/pooja/reset-video-played/${resetBookingId}`, {}, {
        headers: { Authorization: token },
      });
      toast.success("Video played status reset successfully");
      fetchBookings();
      if (selectedPooja && (selectedPooja._id === resetBookingId || selectedPooja.id === resetBookingId)) {
        setSelectedPooja(prev => ({ ...prev, isVideoPlayed: false }));
      }
      setResetVideoDialogOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to reset video played status");
    } finally {
      setResetLoading(false);
    }
  };

  const handleOpenEditDialog = (row) => {
    setEditFormData({
      id: row._id || row.id,
      fullName: row.fullName && row.fullName !== "null" ? row.fullName : "",
      gotra: row.gotra && row.gotra !== "null" ? row.gotra : "",
      dateOfBirth: row.dateOfBirth && row.dateOfBirth !== "null" ? new Date(row.dateOfBirth).toISOString().split('T')[0] : "",
      nakshatra: row.nakshatra && row.nakshatra !== "null" ? row.nakshatra : "",
      sankalp: row.sankalp && row.sankalp !== "null" ? row.sankalp : "",
      purposeOfPooja: row.purposeOfPooja && row.purposeOfPooja !== "null" ? row.purposeOfPooja : "",
      familyMemberNames: row.familyMemberNames && row.familyMemberNames !== "null" ? row.familyMemberNames : "",
      additionalNotes: row.additionalNotes && row.additionalNotes !== "null" ? row.additionalNotes : ""
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    setEditLoading(true);
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const payload = {
        fullName: editFormData.fullName,
        gotra: editFormData.gotra,
        dateOfBirth: editFormData.dateOfBirth,
        nakshatra: editFormData.nakshatra,
        sankalp: editFormData.sankalp,
        purposeOfPooja: editFormData.purposeOfPooja,
        familyMemberNames: editFormData.familyMemberNames,
        additionalNotes: editFormData.additionalNotes
      };

      await axios.patch(`${process.env.REACT_APP_BASEURL}/admin/pooja/personalize/${editFormData.id}`, payload, {
        headers: { Authorization: token },
      });
      toast.success("Pooja details updated successfully");
      setEditDialogOpen(false);
      fetchBookings();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to update pooja details");
    } finally {
      setEditLoading(false);
    }
  };

  const columns = [

    {
      field: 'actions',
      headerName: 'Action',
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const canReassign = params.row.status === 'Accepted' || params.row.status === 'Rejected' || params.row.status === 'Pending';
        return (
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
            <IconButton color="primary" size="small" onClick={() => handleOpenVerifyDialog(params.row)} title="View Details">
              <BsEye />
            </IconButton>
            {(params.row.status === 'Pending' || params.row.status === 'Accepted') && (
              <IconButton color="info" size="small" onClick={() => handleOpenEditDialog(params.row)} title="Edit Details">
                <BsPencil />
              </IconButton>
            )}
            {canReassign && (
              params.row.isReassigned ? (
                <Typography sx={{ fontSize: '11px', color: '#ff9800', fontWeight: 600, ml: 0.5, border: '1px solid #ffe0b2', padding: '2px 6px', borderRadius: '4px', backgroundColor: '#fff3e0' }}>
                  Reassigned
                </Typography>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                  onClick={() => handleOpenMapping(params.row)}
                >
                  Reassign
                </Button>
              )
            )}
            {params.row.isVideoPlayed && (
              <Button
                variant="outlined"
                size="small"
                color="warning"
                sx={{ textTransform: 'none', fontSize: '12px' }}
                onClick={() => handleResetVideoPlayed(params.row._id || params.row.id)}
              >
                Reset Video
              </Button>
            )}
          </Box>
        );
      }
    },
    { field: 'orderId', headerName: 'Order ID', width: 180, valueGetter: (value, row) => row?.orderId || '-' },
    {
      field: 'poojaName',
      headerName: 'Pooja Name',
      flex: 1,
      minWidth: 200,
      valueGetter: (value, row) => row?.poojaId?.name || row?.poojaName || '-'
    },
    {
      field: 'userName',
      headerName: 'User Name',
      flex: 1,
      minWidth: 200,
      valueGetter: (value, row) => row?.userId?.fullName || '-'
    },
    {
      field: 'astrologerName',
      headerName: 'Astrologer Name',
      flex: 1,
      minWidth: 200,
      valueGetter: (value, row) => row?.astrologerId?.fullName || row?.astrologerName || '-'
    },
    {
      field: 'poojaPrice',
      headerName: 'Pooja Price',
      width: 120,
      valueGetter: (value, row) => row?.poojaPrice ? `₹${row.poojaPrice}` : '-'
    },
    {
      field: 'basePrice',
      headerName: 'Base Price',
      width: 120,
      valueGetter: (value, row) => row?.poojaId?.basePrice ? `₹${row.poojaId.basePrice}` : '-'
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        let color = 'default';
        if (params.row.status === 'Completed') color = 'success';
        if (params.row.status === 'Accepted') color = 'primary';
        if (params.row.status === 'Rejected' || params.row.status === 'Cancelled') color = 'error';
        if (params.row.status === 'Refunded') color = 'warning';
        return <Chip label={params.row.status || '-'} color={color} size="small" />
      }
    },
    {
      field: 'paymentStatus',
      headerName: 'Payment Status',
      width: 130,
      renderCell: (params) => {
        let color = 'default';
        if (params.row.paymentStatus === 'paid' || params.row.paymentStatus === 'wallet_only') color = 'success';
        if (params.row.paymentStatus === 'failed') color = 'error';
        return <Chip label={params.row.paymentStatus || '-'} color={color} size="small" sx={{ textTransform: 'capitalize' }}/>
      }
    },
    {
      field: 'verifiedByAdmin',
      headerName: 'Verified',
      width: 100,
      valueGetter: (value, row) => row?.verifiedByAdmin ? 'Yes' : 'No'
    },
    {
      field: 'isCompletedByAstro',
      headerName: 'Astro Completed',
      width: 140,
      valueGetter: (value, row) => row?.isCompletedByAstro ? 'Yes' : 'No'
    },
    {
      field: 'isMediaUploaded',
      headerName: 'Media Uploaded',
      width: 130,
      valueGetter: (value, row) => row?.isMediaUploaded ? 'Yes' : 'No'
    },
    {
      field: 'isVideoPlayed',
      headerName: 'Video Played',
      width: 120,
      valueGetter: (value, row) => row?.isVideoPlayed ? 'Yes' : 'No'
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 180,
      valueGetter: (value, row) => row?.createdAt ? new Date(row.createdAt).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '-'
    },
  ];

  return (
    <div className="container-xxl">
      <PageHeader1
        headerTitle="Pooja Bookings"
        isExport={false}
      />

      <Box sx={{ width: '100%', bgcolor: 'background.paper', p: 2, borderRadius: 2, boxShadow: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Bookings List
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Select
              size="small"
              value={searchBy}
              onChange={(e) => setSearchBy(e.target.value)}
              sx={{ minWidth: 140, bgcolor: 'white' }}
            >
              {SEARCH_BY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              placeholder={`Search by ${SEARCH_BY_OPTIONS.find(o => o.value === searchBy)?.label}...`}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleApplySearch();
                }
              }}
            />
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              size="small"
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              size="small"
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />
            <Button variant="contained" color="primary" onClick={handleApplySearch} size="small" sx={{ px: 3 }}>
              Search
            </Button>
            <Button variant="outlined" onClick={handleResetSearch}>
              Reset
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : <BsDownload />}
              onClick={handleExport}
              disabled={exportLoading}
            >
              {exportLoading ? "Exporting..." : "Export"}
            </Button>
          </Box>
        </Box>
        <Box sx={{ borderTop: 1, borderBottom: 1, borderColor: '#cfcfcf', py: 1, my: 2 }}>
          <Tabs
            value={searchStatus || "All"}
            onChange={(e, newValue) => {
              const status = newValue === "All" ? "" : newValue;
              setSearchStatus(status);
              setAppliedStatus(status);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '14px' }
            }}
          >
            <Tab label="All Status" value="All" />
            {["Pending", "Accepted", /* "Scheduled", */ "Started", "Ongoing", "Completed", "Rejected", "Cancelled", "Refunded"].map(status => (
              <Tab key={status} label={status} value={status} />
            ))}
          </Tabs>
        </Box>
        <DataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row._id || row.id}
          rowCount={totalCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          disableRowSelectionOnClick
          autoHeight
        />
      </Box>

      <Dialog open={verifyDialogOpen} onClose={handleCloseVerifyDialog} maxWidth="md" fullWidth>
        <DialogTitle>View Pooja Details</DialogTitle>
        <DialogContent dividers>
          {selectedPooja && (
            <Box mb={3}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Booking Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Order ID</Typography>
                  <Typography variant="body2">{selectedPooja.orderId || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Status</Typography>
                  <Typography variant="body2" color={
                    selectedPooja.status === 'Completed' ? 'green' :
                      (selectedPooja.status === 'Rejected' || selectedPooja.status === 'Cancelled') ? 'red' : 'primary'
                  } fontWeight={600}>
                    {selectedPooja.status || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Pooja Price</Typography>
                  <Typography variant="body2">₹{selectedPooja.poojaPrice || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Base Price</Typography>
                  <Typography variant="body2">₹{selectedPooja.poojaId?.basePrice || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Payment Status</Typography>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{selectedPooja.paymentStatus || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Verified By Admin</Typography>
                  <Typography variant="body2">{selectedPooja.verifiedByAdmin ? 'Yes' : 'No'}</Typography>
                </Grid>
                {selectedPooja.poojaId?.name && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Pooja Name</Typography>
                    <Typography variant="body2">{selectedPooja.poojaId.name}</Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Customer Name</Typography>
                  <Typography variant="body2">{selectedPooja.userId?.fullName || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Customer Mobile</Typography>
                  <Typography variant="body2">{selectedPooja.userId?.mobileNumber || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Customer Email</Typography>
                  <Typography variant="body2">{selectedPooja.userId?.email || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Astrologer Name</Typography>
                  <Typography variant="body2">{selectedPooja.astrologerId?.fullName || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Astrologer Mobile</Typography>
                  <Typography variant="body2">{selectedPooja.astrologerId?.mobileNumber || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Media Uploaded</Typography>
                  <Typography variant="body2">{selectedPooja.isMediaUploaded ? 'Yes' : 'No'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Video Played</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">{selectedPooja.isVideoPlayed ? 'Yes' : 'No'}</Typography>
                    {selectedPooja.isVideoPlayed && (
                      <Button
                        size="small"
                        color="warning"
                        variant="text"
                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                        onClick={() => handleResetVideoPlayed(selectedPooja._id || selectedPooja.id)}
                      >
                        Reset
                      </Button>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="textSecondary" display="block">Completed By Astro</Typography>
                  <Typography variant="body2">{selectedPooja.isCompletedByAstro ? 'Yes' : 'No'}</Typography>
                </Grid>
                {selectedPooja.completedByAstroAt && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Astro Completed At</Typography>
                    <Typography variant="body2">{new Date(selectedPooja.completedByAstroAt).toLocaleString()}</Typography>
                  </Grid>
                )}
                {selectedPooja.invoiceNumber && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Invoice Number</Typography>
                    <Typography variant="body2">{selectedPooja.invoiceNumber}</Typography>
                  </Grid>
                )}
                {selectedPooja.invoicePdfUrl && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Invoice PDF</Typography>
                    <Typography variant="body2">
                      <a href={selectedPooja.invoicePdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 600 }}>View Invoice</a>
                    </Typography>
                  </Grid>
                )}
                {selectedPooja.address && (
                  <Grid item xs={12} sm={12} md={12}>
                    <Typography variant="caption" color="textSecondary" display="block">Location / Address</Typography>
                    <Typography variant="body2">{selectedPooja.address}</Typography>
                  </Grid>
                )}
                {selectedPooja.scheduledAt && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Scheduled At</Typography>
                    <Typography variant="body2">{new Date(selectedPooja.scheduledAt).toLocaleString()}</Typography>
                  </Grid>
                )}
                {selectedPooja.fullName && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Booking Name</Typography>
                    <Typography variant="body2">{selectedPooja.fullName}</Typography>
                  </Grid>
                )}
                {selectedPooja.dateOfBirth && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Date of Birth</Typography>
                    <Typography variant="body2">{new Date(selectedPooja.dateOfBirth).toLocaleDateString()}</Typography>
                  </Grid>
                )}
                {selectedPooja.gotra && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Gotra</Typography>
                    <Typography variant="body2">{selectedPooja.gotra}</Typography>
                  </Grid>
                )}
                {selectedPooja.nakshatra && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Nakshatra</Typography>
                    <Typography variant="body2">{selectedPooja.nakshatra}</Typography>
                  </Grid>
                )}
                {(selectedPooja.familyMemberNames && selectedPooja.familyMemberNames !== "null" && selectedPooja.familyMemberNames.trim() !== "") && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Family Members</Typography>
                    <Typography variant="body2">{selectedPooja.familyMemberNames}</Typography>
                  </Grid>
                )}
                {(selectedPooja.purposeOfPooja && selectedPooja.purposeOfPooja !== "null" && selectedPooja.purposeOfPooja.trim() !== "") && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Purpose</Typography>
                    <Typography variant="body2">{selectedPooja.purposeOfPooja}</Typography>
                  </Grid>
                )}
                {(selectedPooja.sankalp && selectedPooja.sankalp !== "null" && selectedPooja.sankalp.trim() !== "") && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Sankalp</Typography>
                    <Typography variant="body2">{selectedPooja.sankalp}</Typography>
                  </Grid>
                )}
                {(selectedPooja.additionalNotes && selectedPooja.additionalNotes !== "null" && selectedPooja.additionalNotes.trim() !== "") && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" display="block">Additional Notes</Typography>
                    <Typography variant="body2">{selectedPooja.additionalNotes}</Typography>
                  </Grid>
                )}
                {(selectedPooja.acceptMessage && selectedPooja.acceptMessage !== "null" && selectedPooja.acceptMessage.trim() !== "") && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" display="block">Accept Message</Typography>
                    <Typography variant="body2">{selectedPooja.acceptMessage}</Typography>
                  </Grid>
                )}
                {(selectedPooja.rejectionReason && selectedPooja.rejectionReason !== "null" && selectedPooja.rejectionReason.trim() !== "") && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" display="block">Rejection Reason</Typography>
                    <Typography variant="body2" color="error">{selectedPooja.rejectionReason}</Typography>
                  </Grid>
                )}
                {(selectedPooja.cancellationReason && selectedPooja.cancellationReason !== "null" && selectedPooja.cancellationReason.trim() !== "") && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" display="block">Cancellation Reason</Typography>
                    <Typography variant="body2" color="error">{selectedPooja.cancellationReason}</Typography>
                  </Grid>
                )}
                {(selectedPooja.adminMediaRejection && selectedPooja.adminMediaRejection !== "null" && selectedPooja.adminMediaRejection.trim() !== "") && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" display="block">Admin Media Rejection Note</Typography>
                    <Typography variant="body2" color="error">{selectedPooja.adminMediaRejection}</Typography>
                  </Grid>
                )}
                {(selectedPooja.completionNotes && selectedPooja.completionNotes !== "null" && selectedPooja.completionNotes.trim() !== "") && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" display="block">Completion Notes</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedPooja.completionNotes}</Typography>
                  </Grid>
                )}
              </Grid>
              <Divider sx={{ mt: 2 }} />
            </Box>
          )}

          {proofsState.length === 0 ? (
            <Typography>No proofs available for this pooja.</Typography>
          ) : (
            <Grid container spacing={2}>
              {proofsState.map((proof, index) => {
                const isVideo = proof.url.toLowerCase().endsWith('.mp4') || proof.url.toLowerCase().includes('video');
                return (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      {isVideo ? (
                        <CardMedia
                          component="video"
                          controls
                          src={proof.url}
                          sx={{ height: 200, objectFit: 'contain', bgcolor: 'black' }}
                        />
                      ) : (
                        <CardMedia
                          component="img"
                          image={proof.url}
                          alt={`Proof ${index + 1}`}
                          sx={{ height: 200, objectFit: 'contain', bgcolor: '#f5f5f5' }}
                        />
                      )}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVerifyDialog} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mapOpen} onClose={() => setMapOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Reassign Astrologer</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {mapRowData && (
            <Box mb={3} p={2} sx={{ bgcolor: "#f5f5f5", borderRadius: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Order ID</Typography>
                  <Typography fontWeight={500}>{mapRowData.orderId || "-"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Pooja Name</Typography>
                  <Typography fontWeight={500}>{mapRowData.poojaId?.name || mapRowData.poojaName || "-"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">User Name</Typography>
                  <Typography fontWeight={500}>
                    {mapRowData.userId?.fullName || mapRowData.userName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Current Astrologer</Typography>
                  <Typography fontWeight={500}>{mapRowData.astrologerId?.fullName || mapRowData.astrologerName || "None"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Typography fontWeight={500}>
                    {mapRowData.status || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          <Box sx={{ border: "1px solid #ddd", p: 2, mb: 2, borderRadius: 2 }}>
            <Grid container spacing={2}>
              <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" px={2} pt={1} pb={1}>
                <Button
                  size="small"
                  disabled={astroPage <= 1}
                  onClick={() => {
                    const page = astroPage - 1;
                    setAstroPage(page);
                    fetchAstrologers(page, astroSearch);
                  }}
                >
                  Previous
                </Button>
                <Typography variant="body2">
                  Page {astroPage} / {astroPagination.totalPages}
                </Typography>
                <Button
                  size="small"
                  disabled={astroPage >= astroPagination.totalPages}
                  onClick={() => {
                    const page = astroPage + 1;
                    setAstroPage(page);
                    fetchAstrologers(page, astroSearch);
                  }}
                >
                  Next
                </Button>
              </Box>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  {(() => {
                    const basePrice = Number(mapRowData?.poojaId?.basePrice || mapRowData?.basePrice) || 0;
                    const duration = Number(mapRowData?.poojaId?.duration || mapRowData?.duration) || 0;
                    const selectedAstro = astrologers.find(a => (a._id === selectedAstrologerId || a.astrologerId?._id === selectedAstrologerId));
                    const mappedPrice = selectedAstro?.finalOfferUserPrice ?? selectedAstro?.finalUserPrice ?? selectedAstro?.price;
                    const astroCost = Number(selectedAstro?.videoCall?.offerPricePerMinute) || 0;
                    const suggestivePrice = basePrice + (duration * astroCost);

                    if (!selectedAstro) return null;

                    return (
                      <Box display="flex" alignItems="center" gap={2}>
                        {mappedPrice !== undefined && mappedPrice !== null && (
                          <Typography
                            variant="caption"
                            color="success.main"
                            sx={{ fontWeight: 600 }}
                          >
                            {`Pooja Price: ₹${mappedPrice} ${selectedAstro?.finalUserPrice && selectedAstro?.finalOfferUserPrice && selectedAstro.finalUserPrice !== selectedAstro.finalOfferUserPrice ? `(MRP: ₹${selectedAstro.finalUserPrice})` : ''}`}
                          </Typography>
                        )}
                        {astroCost > 0 && (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{ fontWeight: 500 }}
                          >
                            {`{Suggestive Offer Price: = ${basePrice} + (${duration} * ${astroCost}) = ${suggestivePrice}}`}
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}
                </Box>
                <Autocomplete
                  loading={astroLoading}
                  options={astrologers}
                  filterOptions={(x) => x}
                  getOptionLabel={(option) => {
                    const name = option?.fullName || option?.astrologerId?.fullName || "";
                    const phone = option?.mobileNumber || option?.astrologerId?.mobileNumber || "-";
                    const price = option?.finalOfferUserPrice ?? option?.finalUserPrice ?? option?.price ?? option?.videoCall?.offerPricePerMinute ?? "-";
                    return `${name} (Phone: ${phone} | Price: ₹${price})`;
                  }}
                  isOptionEqualToValue={(option, val) =>
                    (option?._id || option?.astrologerId?._id) === (val?._id || val?.astrologerId?._id)
                  }
                  value={astrologers.find((a) => (a._id === selectedAstrologerId || a.astrologerId?._id === selectedAstrologerId)) || null}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      debouncedAstroSearch(newInputValue);
                    }
                  }}
                  onChange={(e, value) => {
                    setSelectedAstrologerId(value?.astrologerId?._id || value?._id || "");
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Search Astrologer" size="small" />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setMapOpen(false)} variant="outlined" disabled={mapLoading}>
            Cancel
          </Button>
          <Button onClick={handleMappingSubmit} variant="contained" color="success" disabled={mapLoading}>
            {mapLoading ? <CircularProgress size={20} color="inherit" /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetVideoDialogOpen} onClose={() => setResetVideoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Video Played Status</DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to reset the 'Video Played' status for this booking?
            This will allow the user to play the video again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setResetVideoDialogOpen(false)} variant="outlined" disabled={resetLoading}>
            Cancel
          </Button>
          <Button onClick={confirmResetVideoPlayed} variant="contained" color="warning" disabled={resetLoading}>
            {resetLoading ? <CircularProgress size={20} color="inherit" /> : "Reset"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Pooja Personalization Details</DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                size="small"
                value={editFormData.fullName}
                onChange={(e) => setEditFormData({ ...editFormData, fullName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Gotra"
                size="small"
                value={editFormData.gotra}
                onChange={(e) => setEditFormData({ ...editFormData, gotra: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={editFormData.dateOfBirth}
                onChange={(e) => setEditFormData({ ...editFormData, dateOfBirth: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nakshatra"
                size="small"
                value={editFormData.nakshatra}
                onChange={(e) => setEditFormData({ ...editFormData, nakshatra: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Family Member Names"
                size="small"
                value={editFormData.familyMemberNames}
                onChange={(e) => setEditFormData({ ...editFormData, familyMemberNames: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Purpose of Pooja"
                size="small"
                value={editFormData.purposeOfPooja}
                onChange={(e) => setEditFormData({ ...editFormData, purposeOfPooja: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Sankalp"
                size="small"
                multiline
                rows={2}
                value={editFormData.sankalp}
                onChange={(e) => setEditFormData({ ...editFormData, sankalp: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                size="small"
                multiline
                rows={2}
                value={editFormData.additionalNotes}
                onChange={(e) => setEditFormData({ ...editFormData, additionalNotes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setEditDialogOpen(false)} variant="outlined" disabled={editLoading}>
            Cancel
          </Button>
          <Button onClick={handleEditSubmit} variant="contained" color="primary" disabled={editLoading}>
            {editLoading ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
}

export default PoojaBookings;
